import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileSpreadsheet, Loader2, Truck, MapPin, ChevronDown, ChevronRight, RefreshCw, Navigation, Info } from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import {
  arrivalTrackingService,
  type ArrivalTrackingData,
  type ArrivalHistoryRow,
  type ArrivalTrackingShipment,
  type ArrivalPoint,
} from '../services/arrivalTrackingService';

const formatDateTimeTr = (value: string | null | undefined) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const EMPTY_DATA: ArrivalTrackingData = {
  settings: { geofenceKm: 2, transitionDate: '', customerCutoffTime: '13:00', depotCutoffTime: '13:00' },
  generatedAt: '',
  rows: [],
};

const pointStateLabel = (point: ArrivalPoint): { label: string; className: string } => {
  if (point.arrived) return { label: 'Varış', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' };
  if (point.current_state === 'no-location') return { label: 'Konum Yok', className: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400' };
  if (point.current_state === 'no-coordinates') return { label: 'Koordinat Yok', className: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300' };
  return { label: 'Yolda', className: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300' };
};

export function ArrivalTracking() {
  const today = useMemo(() => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }, []);
  const monthStart = useMemo(() => `${today.slice(0, 8)}01`, [today]);
  const [data, setData] = useState<ArrivalTrackingData>(EMPTY_DATA);
  const [history, setHistory] = useState<ArrivalHistoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [plateFilter, setPlateFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [activeTab, setActiveTab] = useState<'active' | 'history'>('active');
  const [historyFrom, setHistoryFrom] = useState(monthStart);
  const [historyTo, setHistoryTo] = useState(today);

  const loadData = useCallback(async (silent = false) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError('');
      const response = await arrivalTrackingService.getList();
      setData(response?.data || EMPTY_DATA);
      setLoaded(true);
    } catch (err: any) {
      console.error('Varış takibi yüklenemedi:', err);
      setError(err?.message || 'Varış takibi alınırken bir hata oluştu');
      setLoaded(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const loadHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      setError('');
      const response = await arrivalTrackingService.getHistory({
        from: historyFrom,
        to: historyTo,
        plate: plateFilter.trim() || undefined,
      });
      setHistory(Array.isArray(response?.data) ? response.data : []);
    } catch (err: any) {
      console.error('Varış geçmişi yüklenemedi:', err);
      setError(err?.message || 'Varış geçmişi alınırken bir hata oluştu');
    } finally {
      setHistoryLoading(false);
    }
  }, [historyFrom, historyTo, plateFilter]);

  useEffect(() => {
    if (activeTab !== 'history') return;
    void loadHistory();
  }, [activeTab, loadHistory]);

  const filteredRows = useMemo(() => {
    const plate = plateFilter.trim().toUpperCase();
    const owner = ownerFilter;
    const state = stateFilter;
    return data.rows.filter((shipment) => {
      if (plate && !String(shipment.plate || '').toUpperCase().includes(plate)) return false;
      if (owner && shipment.owner_type !== owner) return false;
      if (state) {
        if (state === 'arrived' && !shipment.points.some((point) => point.arrived)) return false;
        if (state === 'onway' && !shipment.points.some((point) => !point.arrived && point.current_state !== 'no-location' && point.current_state !== 'no-coordinates')) return false;
        if (state === 'no-location' && !shipment.points.some((point) => point.current_state === 'no-location' || point.current_state === 'no-coordinates')) return false;
      }
      return true;
    });
  }, [data.rows, plateFilter, ownerFilter, stateFilter]);

  const summary = useMemo(() => {
    const allPoints = data.rows.flatMap((shipment) => shipment.points);
    return {
      shipmentCount: data.rows.length,
      arrivedPoints: allPoints.filter((point) => point.arrived).length,
      onwayPoints: allPoints.filter((point) => !point.arrived && point.current_state === 'not-arrived').length,
      noLocationPoints: allPoints.filter((point) => point.current_state === 'no-location' || point.current_state === 'no-coordinates').length,
    };
  }, [data.rows]);

  const filteredHistory = useMemo(() => {
    const plate = plateFilter.trim().toUpperCase();
    return history.filter((row) => {
      if (plate && !String(row.plate || '').toUpperCase().includes(plate)) return false;
      if (ownerFilter && row.owner_type !== ownerFilter) return false;
      return true;
    });
  }, [history, ownerFilter, plateFilter]);

  const historyPointSummary = useMemo(() => {
    const grouped = new Map<string, { pointName: string; count: number; lastArrivalAt: string | null }>();
    for (const row of filteredHistory) {
      const key = String(row.point_name || '-');
      const current = grouped.get(key) || { pointName: key, count: 0, lastArrivalAt: null };
      current.count += 1;
      if (!current.lastArrivalAt || new Date(row.arrived_at || 0).getTime() > new Date(current.lastArrivalAt || 0).getTime()) {
        current.lastArrivalAt = row.arrived_at || null;
      }
      grouped.set(key, current);
    }
    return Array.from(grouped.values())
      .sort((a, b) => b.count - a.count || new Date(b.lastArrivalAt || 0).getTime() - new Date(a.lastArrivalAt || 0).getTime())
      .slice(0, 4);
  }, [filteredHistory]);

  const exportExcel = () => {
    const workbook = XLSX.utils.book_new();

    const rows = activeTab === 'active'
      ? filteredRows.flatMap((shipment: ArrivalTrackingShipment) =>
        shipment.points.map((point) => ({
          'Sevkiyat No': shipment.shipment_no,
          'Plaka': shipment.plate,
          'Sürücü': shipment.driver_name,
          'Tür': shipment.owner_type === 'own' ? 'Özmal' : 'Spot',
          'Teslim Noktası': point.point_name,
          'İl': point.city,
          'İlçe': point.district,
          'Mesafe (km)': point.distance_km,
          'Durum': point.arrived ? 'Varış' : point.current_state === 'no-location' ? 'Konum Yok' : point.current_state === 'no-coordinates' ? 'Koordinat Yok' : 'Yolda',
          'Varış Zamanı': formatDateTimeTr(point.arrived_at),
          'İlk Görülme': formatDateTimeTr(point.first_seen_at),
          'Son Görülme': formatDateTimeTr(point.last_seen_at),
          'Konum Kaynağı': point.location_source === 'seyir' ? 'Seyir' : point.location_source === 'mobile' ? 'Mobil' : 'Diğer',
          'Nokta Tamamlandı': point.is_completed ? 'Evet' : 'Hayır',
        }))
      )
      : filteredHistory.map((row) => ({
        'Sevkiyat No': row.shipment_no,
        'Plaka': row.plate,
        'Sürücü': row.driver_name,
        'Tür': row.owner_type === 'own' ? 'Özmal' : 'Spot',
        'Teslim Noktası': row.point_name,
        'Yükleme Noktası': row.pickup_name,
        'Yükleme İli': row.pickup_city,
        'Teslim İli': row.delivery_city,
        'Mesafe (km)': row.distance_km,
        'Varış Zamanı': formatDateTimeTr(row.arrived_at),
        'Konum Kaynağı': row.location_source === 'seyir' ? 'Seyir' : row.location_source === 'mobile' ? 'Mobil' : 'Diğer',
      }));

    const sheet = XLSX.utils.json_to_sheet(rows);
    sheet['!cols'] = [{ wch: 14 }, { wch: 14 }, { wch: 24 }, { wch: 10 }, { wch: 28 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 17 }, { wch: 17 }, { wch: 17 }, { wch: 12 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(workbook, sheet, 'Varış Takibi');

    XLSX.writeFile(workbook, `varis-takibi-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const toggleExpanded = (shipmentId: number) => {
    setExpanded((current) => ({ ...current, [shipmentId]: !current[shipmentId] }));
  };

  const summaryCards = [
    { label: 'Aktif Sevkiyat', value: String(summary.shipmentCount), icon: Truck, color: 'bg-blue-50 text-blue-700' },
    { label: 'Varış Tamamlanan Nokta', value: String(summary.arrivedPoints), icon: MapPin, color: 'bg-emerald-50 text-emerald-700' },
    { label: 'Yoldaki Nokta', value: String(summary.onwayPoints), icon: Navigation, color: 'bg-amber-50 text-amber-700' },
    { label: 'Konum Olmayan Nokta', value: String(summary.noLocationPoints), icon: Info, color: 'bg-slate-50 text-slate-700' },
  ];

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-slate-100">Varış Takibi</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
            Özmal (Seyir) ve spot (mobil) konum kaynakları ile aktif sevkiyatların çoklu teslim noktalarına varış durumları.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void loadData(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Yenile
          </button>
          <button
            type="button"
            onClick={exportExcel}
            disabled={loading || data.rows.length === 0}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            <FileSpreadsheet className="h-4 w-4" />
            Excele Aktar
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-[#141414]">
        <div className="mb-4 flex gap-2">
          <button
            type="button"
            onClick={() => setActiveTab('active')}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${activeTab === 'active' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-300'}`}
          >
            Aktif Takip
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('history')}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${activeTab === 'history' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-300'}`}
          >
            Varış Geçmişi
          </button>
        </div>

        <div className={`grid gap-4 ${activeTab === 'history' ? 'md:grid-cols-5' : 'md:grid-cols-3'}`}>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Plaka</label>
            <input type="text" value={plateFilter} onChange={(e) => setPlateFilter(e.target.value)} placeholder="Örn: 34 ABC 123" className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200" />
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Araç Tipi</label>
            <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
              <option value="">Tümü</option>
              <option value="own">Özmal</option>
              <option value="spot">Spot</option>
            </select>
          </div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Durum</label>
            <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
              <option value="">Tümü</option>
              <option value="arrived">Varış Olan</option>
              <option value="onway">Yolda Olan</option>
              <option value="no-location">Konumu Olmayan</option>
            </select>
          </div>
          {activeTab === 'history' && (
            <>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Başlangıç Tarihi</label>
                <input type="date" value={historyFrom} onChange={(e) => setHistoryFrom(e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200" />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Bitiş Tarihi</label>
                <div className="flex gap-2">
                  <input type="date" value={historyTo} onChange={(e) => setHistoryTo(e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200" />
                  <button
                    type="button"
                    onClick={() => void loadHistory()}
                    disabled={historyLoading}
                    className="inline-flex h-10 items-center justify-center rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                  >
                    {historyLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Getir'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-blue-50 p-3 text-xs text-blue-800 dark:bg-blue-950/50 dark:text-blue-300">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            Geofence yarıçapı: <strong>{data.settings.geofenceKm} km</strong>. Özmal araçlar için konum kaynağı Seyir, spot araçlar için mobil uygulama konumudur.
            Çoklu teslim noktaları sevkiyata tanımlı güzergâhlardan (<code>shipment_routes</code>) alınır; tanımlı değilse sevkiyat teslim adresi kullanılır.
            Son güncelleme: {data.generatedAt ? formatDateTimeTr(data.generatedAt) : '-'}
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-4">
        {summaryCards.map((card) => (
          <div key={card.label} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-[#141414]">
            <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${card.color}`}>
              <card.icon className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{card.label}</div>
              <div className="text-xl font-bold text-slate-900 dark:text-slate-100">{card.value}</div>
            </div>
          </div>
        ))}
      </div>

      {loaded && activeTab === 'active' && (
        <div className="space-y-3">
          {filteredRows.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-400 dark:border-slate-800 dark:bg-[#141414]">
              Kayıt bulunamadı
            </div>
          ) : filteredRows.map((shipment) => {
            const isExpanded = !!expanded[shipment.shipment_id];
            const arrivedCount = shipment.points.filter((point) => point.arrived).length;
            return (
              <div key={shipment.shipment_id} className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-[#141414]">
                <button
                  type="button"
                  onClick={() => toggleExpanded(shipment.shipment_id)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-900"
                >
                  {isExpanded ? <ChevronDown className="h-5 w-5 shrink-0 text-slate-400" /> : <ChevronRight className="h-5 w-5 shrink-0 text-slate-400" />}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-slate-900 dark:text-slate-100">{shipment.shipment_no || `#${shipment.shipment_id}`}</span>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${shipment.owner_type === 'own' ? 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300' : 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300'}`}>
                        {shipment.owner_type === 'own' ? 'Özmal' : 'Spot'}
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      {shipment.plate} · {shipment.driver_name} · {shipment.pickup_city} → {shipment.delivery_city}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${arrivedCount === shipment.points.length && shipment.points.length > 0 ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300'}`}>
                      {arrivedCount}/{shipment.points.length} Varış
                    </span>
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-slate-100 dark:border-slate-800">
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900 dark:text-slate-400">
                          <tr>
                            <th className="px-4 py-2.5 font-semibold">Teslim Noktası</th>
                            <th className="px-4 py-2.5 font-semibold">İl / İlçe</th>
                            <th className="px-4 py-2.5 text-right font-semibold">Mesafe (km)</th>
                            <th className="px-4 py-2.5 font-semibold">Durum</th>
                            <th className="px-4 py-2.5 font-semibold">Varış Zamanı</th>
                            <th className="px-4 py-2.5 font-semibold">Kaynak</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                          {shipment.points.map((point, index) => {
                            const state = pointStateLabel(point);
                            return (
                              <tr key={`${shipment.shipment_id}-${point.route_id ?? index}`} className="hover:bg-slate-50 dark:hover:bg-slate-900">
                                <td className="px-4 py-2.5">
                                  <div className="font-medium text-slate-800 dark:text-slate-100">{point.point_name}</div>
                                  <div className="text-xs text-slate-400">{point.address}</div>
                                  {point.is_completed && <div className="mt-0.5 text-xs text-emerald-600 dark:text-emerald-400">Nokta tamamlandı</div>}
                                </td>
                                <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">{point.city}{point.district ? ` / ${point.district}` : ''}</td>
                                <td className="px-4 py-2.5 text-right text-slate-600 dark:text-slate-300">{point.distance_km != null ? point.distance_km.toLocaleString('tr-TR', { maximumFractionDigits: 2 }) : '-'}</td>
                                <td className="px-4 py-2.5">
                                  <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${state.className}`}>{state.label}</span>
                                </td>
                                <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">{formatDateTimeTr(point.arrived_at)}</td>
                                <td className="px-4 py-2.5">
                                  <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${point.location_source === 'seyir' ? 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300' : point.location_source === 'mobile' ? 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300' : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'}`}>
                                    {point.location_source === 'seyir' ? 'Seyir' : point.location_source === 'mobile' ? 'Mobil' : 'Diğer'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-4">
            <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-[#141414]">
              <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Geçmiş Varış</div>
              <div className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">{filteredHistory.length}</div>
            </div>
            {historyPointSummary.map((item) => (
              <div key={item.pointName} className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-[#141414]">
                <div className="truncate text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Nokta</div>
                <div className="mt-1 truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{item.pointName}</div>
                <div className="mt-2 text-xl font-bold text-slate-900 dark:text-slate-100">{item.count}</div>
                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">Son varış: {formatDateTimeTr(item.lastArrivalAt)}</div>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-[#141414]">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-3 font-semibold">Varış Zamanı</th>
                  <th className="px-4 py-3 font-semibold">Sevkiyat No</th>
                  <th className="px-4 py-3 font-semibold">Plaka</th>
                  <th className="px-4 py-3 font-semibold">Sürücü</th>
                  <th className="px-4 py-3 font-semibold">Tür</th>
                  <th className="px-4 py-3 font-semibold">Teslim Noktası</th>
                  <th className="px-4 py-3 font-semibold">Yükleme</th>
                  <th className="px-4 py-3 text-right font-semibold">Mesafe (km)</th>
                  <th className="px-4 py-3 font-semibold">Kaynak</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {historyLoading ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td>
                  </tr>
                ) : filteredHistory.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-slate-400">Kayıt bulunamadı</td>
                  </tr>
                ) : filteredHistory.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-900">
                    <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">{formatDateTimeTr(row.arrived_at)}</td>
                    <td className="px-4 py-2.5 font-medium text-slate-800 dark:text-slate-100">{row.shipment_no}</td>
                    <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">{row.plate}</td>
                    <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">{row.driver_name}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${row.owner_type === 'own' ? 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300' : 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300'}`}>
                        {row.owner_type === 'own' ? 'Özmal' : 'Spot'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">{row.point_name}</td>
                    <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">{row.pickup_name} / {row.pickup_city}</td>
                    <td className="px-4 py-2.5 text-right text-slate-600 dark:text-slate-300">{row.distance_km != null ? Number(row.distance_km).toLocaleString('tr-TR', { maximumFractionDigits: 2 }) : '-'}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${row.location_source === 'seyir' ? 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300' : row.location_source === 'mobile' ? 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300' : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'}`}>
                        {row.location_source === 'seyir' ? 'Seyir' : row.location_source === 'mobile' ? 'Mobil' : 'Diğer'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}
    </div>
  );
}
